import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatSortModule, Sort } from '@angular/material/sort';
import { EstablishmentService } from '../../services/establishment/establishment.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { RealtimeService } from '../../services/realtime/realtime.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { BookingActionDialogComponent } from './booking-action-dialog/booking-action-dialog.component';
import { AllocateDialogComponent } from './allocate-dialog/allocate-dialog.component';
import { AssignedRoomsDialogComponent } from './assigned-rooms-dialog/assigned-rooms-dialog.component';
import { ManualBookingDialogComponent } from './manual-booking-dialog/manual-booking-dialog.component';
import { sortTableRows } from '../../common-files/table-sort';
import { Subscription } from 'rxjs';
import {
  filterBookingRows,
  normalizeBookingSearchFilters,
  validateBookingSearchFilters,
} from '../../common-files/booking-search';
import {
  BOOKING_STATUS,
  BOOKING_LIFECYCLE,
  bookingStatusOf,
  bookingLifecycleOf,
  canAllocateBooking,
  canCancelBooking,
  isConfirmedBooking,
  isPendingBooking,
} from '../../common-files/booking-status';

@Component({
  selector: 'app-establishment-bookings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatMenuModule,
    MatSortModule,
    LoaderComponent,
  ],
  templateUrl: './establishment-bookings.component.html',
  styleUrl: './establishment-bookings.component.scss',
})
export class EstablishmentBookingsComponent implements OnInit, OnDestroy {
  familyCode = new FormControl('');
  fromDate = new FormControl('');
  toDate = new FormControl('');
  guestCount = new FormControl('');
  gender = new FormControl<string>('');
  // one workflow filter: each view maps to a booking-status + lifecycle pair.
  view = new FormControl<string>('all');
  // utsav picker (top right): scopes the list to the utsav and fills the date
  // range from its accommodation window.
  utsav = new FormControl<number | ''>('');
  utsavList: any[] = [];

  readonly genderOptions = [
    { value: '', label: 'Any' },
    { value: 'MALE', label: 'Male' },
    { value: 'FEMALE', label: 'Female' },
    { value: 'BOTH', label: 'Both' },
  ];

  readonly viewOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'checkedin', label: 'Checked-in' },
    { value: 'checkedout', label: 'Checked-out' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'rejected', label: 'Rejected' },
  ];

  // Pending = not yet assigned (lifecycle NEW; the backend also matches NULL);
  // Assigned = still pending but rooms are assigned (lifecycle ALLOCATED).
  private static readonly VIEW_FILTERS: Record<string, { bookingStatus: number | ''; lcStatus: number | '' }> = {
    all: { bookingStatus: '', lcStatus: '' },
    pending: { bookingStatus: BOOKING_STATUS.PENDING, lcStatus: BOOKING_LIFECYCLE.NEW },
    assigned: { bookingStatus: BOOKING_STATUS.PENDING, lcStatus: BOOKING_LIFECYCLE.ALLOCATED },
    confirmed: { bookingStatus: BOOKING_STATUS.CONFIRMED, lcStatus: '' },
    checkedin: { bookingStatus: '', lcStatus: BOOKING_LIFECYCLE.CHECKED_IN },
    checkedout: { bookingStatus: '', lcStatus: BOOKING_LIFECYCLE.CHECKED_OUT },
    cancelled: { bookingStatus: BOOKING_STATUS.CANCELLED, lcStatus: '' },
    rejected: { bookingStatus: BOOKING_STATUS.REJECTED, lcStatus: '' },
  };

  bookings: any[] = [];
  hasSearched: boolean = false;
  isLoading: boolean = false;
  private sortState: Sort = { active: '', direction: '' };
  private searchRequest?: Subscription;
  private rtSub?: Subscription;
  private rtTimer: any = null;

  // the same 'bookings' view is read-only for the Viewer/Volunteer and manageable
  // by higher roles. Assign = can_allocate (Admin + Operator); Confirm = can_manage (Admin).
  canManage: boolean = false;
  canAllocate: boolean = false;
  canView: boolean = false;
  canCreateManual: boolean = false;

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private realtime: RealtimeService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.canManage = this.permissionService.can('bookings', 'can_manage');
    this.canAllocate = this.permissionService.can('bookings', 'can_allocate');
    this.canCreateManual = this.permissionService.can('bookings', 'can_create_manual_mandir_utsav');
    // a user given only manual booking cannot read the list (needs default_rw)
    this.canView = this.permissionService.can('bookings', 'default_rw');
    if (!this.canView) return;
    this.loadUtsavs();
    this.search();
    this.connectRealtime();
  }

  loadUtsavs(): void {
    this.establishmentService.listUtsavs().subscribe({
      next: (res: any) => {
        this.utsavList = Array.isArray(res?.data?.utsavs) ? res.data.utsavs : [];
      },
    });
  }

  // picking an utsav fills From/To from its accommodation window (falls back to
  // the utsav dates) and re-runs the search scoped to that utsav.
  onUtsavChange(): void {
    const picked = this.utsavList.find((u: any) => Number(u.utsavId) === Number(this.utsav.value));
    if (picked) {
      this.fromDate.setValue(picked.accommodationStartDate || picked.utsavStartDate || '');
      this.toDate.setValue(picked.accommodationEndDate || picked.utsavEndDate || '');
    } else {
      this.fromDate.setValue('');
      this.toDate.setValue('');
    }
    this.search();
  }

  ngOnDestroy(): void {
    this.searchRequest?.unsubscribe();
    this.rtSub?.unsubscribe();
    this.realtime.close();
    if (this.rtTimer) clearTimeout(this.rtTimer);
  }

  // refetch (debounced) when another user assigns/confirms in this branch
  private connectRealtime(): void {
    const estId = localStorage.getItem('activeEstablishmentId');
    if (!estId) return;
    this.rtSub = this.realtime.connect(this.realtime.establishmentChannel(estId)).subscribe(() => {
      if (this.rtTimer) clearTimeout(this.rtTimer);
      this.rtTimer = setTimeout(() => this.search(), 400);
    });
  }

  get pageDescription(): string {
    if (this.canManage && this.canAllocate) {
      return 'Search branch bookings, assign rooms, and confirm assigned bookings.';
    }
    if (this.canManage) return 'Search branch bookings and confirm assigned bookings.';
    if (this.canAllocate) return 'Search branch bookings and assign rooms.';
    if (!this.canView) return 'Create a manual utsav booking for a devotee.';
    return 'Search and view branch bookings.';
  }

  // every action available on a row, in display order. The first entry is the
  // inline button (Confirm beats Assign); the rest go to the three-dot menu —
  // unless there is exactly one action, which is then shown directly.
  rowActions(booking: any): Array<{ key: string; label: string; danger: boolean; run: () => void }> {
    const actions: Array<{ key: string; label: string; danger: boolean; run: () => void }> = [];
    const canAssignHere = this.canAllocate && this.canAssign(booking);

    if (this.canManage && this.canConfirm(booking)) {
      actions.push({ key: 'confirm', label: 'Confirm', danger: false, run: () => this.confirm(booking) });
    }
    if (canAssignHere) {
      actions.push({
        key: 'assign',
        label: this.isAssigned(booking) ? 'Review assignment' : 'Assign',
        danger: false,
        run: () => this.assign(booking),
      });
    }
    if (this.canViewRooms(booking)) {
      actions.push({ key: 'viewRooms', label: 'View rooms', danger: false, run: () => this.viewRooms(booking) });
    }
    if (this.canAllocate && this.canModifyAssignment(booking)) {
      actions.push({ key: 'modify', label: 'Modify assignment', danger: false, run: () => this.modify(booking) });
    }
    if (this.canManage && this.isPending(booking)) {
      actions.push({ key: 'reject', label: 'Reject', danger: true, run: () => this.reject(booking) });
    }
    if (this.canManage && this.canCancelAction(booking)) {
      actions.push({ key: 'cancel', label: 'Cancel', danger: true, run: () => this.cancel(booking) });
    }
    if (this.canAllocate && this.canRevoke(booking)) {
      actions.push({ key: 'revoke', label: 'Revoke rooms', danger: true, run: () => this.revoke(booking) });
    }
    return actions;
  }

  isPending(booking: any): boolean {
    return isPendingBooking(booking);
  }

  isConfirmed(booking: any): boolean {
    return isConfirmedBooking(booking);
  }

  isAssigned(booking: any): boolean {
    return bookingLifecycleOf(booking) === BOOKING_LIFECYCLE.ALLOCATED;
  }

  // Assign rooms is available while the booking is still PENDING (not cancelled/
  // rejected/confirmed); a booking is assignable when its lifecycle is NEW or ALLOCATED.
  canAssign(booking: any): boolean {
    const lc = bookingLifecycleOf(booking);
    return bookingStatusOf(booking) === BOOKING_STATUS.PENDING
      && (lc === BOOKING_LIFECYCLE.NEW || lc === BOOKING_LIFECYCLE.ALLOCATED || lc === null);
  }

  // Confirm is available for a PENDING booking that has been ASSIGNED.
  canConfirm(booking: any): boolean {
    return bookingStatusOf(booking) === BOOKING_STATUS.PENDING && this.isAssigned(booking);
  }

  // the assigned-rooms viewer works for anyone once rooms are assigned; the
  // rooms stay viewable (read-only) while the guests are checked in / out.
  canViewRooms(booking: any): boolean {
    const lc = bookingLifecycleOf(booking);
    return lc === BOOKING_LIFECYCLE.ALLOCATED
      || lc === BOOKING_LIFECYCLE.CHECKED_IN
      || lc === BOOKING_LIFECYCLE.CHECKED_OUT;
  }

  // a CONFIRMED booking's rooms are changed via "Modify assignment" (not checked in/out).
  canModifyAssignment(booking: any): boolean {
    return canAllocateBooking(booking);
  }

  // bookings that still hold rooms can have them revoked: cancelled / rejected
  // leftovers, and a confirmed booking (it then falls back to Pending).
  canRevoke(booking: any): boolean {
    const status = bookingStatusOf(booking);
    return (status === BOOKING_STATUS.CANCELLED
      || status === BOOKING_STATUS.REJECTED
      || status === BOOKING_STATUS.CONFIRMED)
      && this.isAssigned(booking);
  }

  canCancelAction(booking: any): boolean {
    return canCancelBooking(booking);
  }

  // the bulk buttons live on the Assigned view only (Admin).
  showAssignedBulkActions(): boolean {
    return this.view.value === 'assigned'
      && this.canManage
      && this.bookings.some((b) => this.canConfirm(b));
  }

  // -- multi-select confirm (Admin, Assigned view): tick rows, then one Confirm.
  selectedIds = new Set<number>();

  get showSelection(): boolean {
    return this.canManage && this.view.value === 'assigned';
  }

  isSelected(booking: any): boolean {
    return this.selectedIds.has(Number(booking.bookingId));
  }

  toggleSelected(booking: any): void {
    const id = Number(booking.bookingId);
    if (this.selectedIds.has(id)) this.selectedIds.delete(id);
    else this.selectedIds.add(id);
  }

  get allSelected(): boolean {
    const rows = this.bookings.filter((b) => this.canConfirm(b));
    return rows.length > 0 && rows.every((b) => this.isSelected(b));
  }

  // a refresh (search, realtime nudge) keeps the ticks that still make sense and
  // silently drops rows that were confirmed, changed or removed in the meantime.
  private pruneSelection(): void {
    const confirmable = new Set(
      this.bookings.filter((b) => this.canConfirm(b)).map((b) => Number(b.bookingId))
    );
    for (const id of Array.from(this.selectedIds)) {
      if (!confirmable.has(id)) this.selectedIds.delete(id);
    }
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedIds.clear();
      return;
    }
    this.selectedIds.clear();
    for (const b of this.bookings) {
      if (this.canConfirm(b)) this.selectedIds.add(Number(b.bookingId));
    }
  }

  onFamilyCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 12);
    if (input.value !== digits) input.value = digits;
    this.familyCode.setValue(digits);
  }

  submitSearch(event: Event): void {
    event.preventDefault();
    this.search();
  }

  search(): void {
    const viewFilter =
      EstablishmentBookingsComponent.VIEW_FILTERS[this.view.value ?? 'all']
      ?? EstablishmentBookingsComponent.VIEW_FILTERS['all'];
    const filters = normalizeBookingSearchFilters({
      familyCode: this.familyCode.value,
      fromDate: this.fromDate.value,
      toDate: this.toDate.value,
      bookingStatus: viewFilter.bookingStatus,
      lcStatus: viewFilter.lcStatus,
      utsavId: this.utsav.value,
      guestCount: this.guestCount.value,
      gender: this.gender.value,
    });

    this.searchRequest?.unsubscribe();
    const validationMessage = validateBookingSearchFilters(filters);
    if (validationMessage) {
      this.bookings = [];
      this.selectedIds.clear();
      this.hasSearched = true;
      this.isLoading = false;
      this.toastService.error(validationMessage);
      return;
    }

    if (this.familyCode.value !== filters.familyCode) {
      this.familyCode.setValue(filters.familyCode);
    }

    this.isLoading = true;
    this.searchRequest = this.establishmentService.listBookings(filters).subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res?.data?.bookings) ? res.data.bookings : [];
        this.bookings = this.applySort(filterBookingRows(rows, filters));
        this.pruneSelection();
        this.hasSearched = true;
        this.isLoading = false;
      },
      error: (err) => {
        this.bookings = [];
        this.selectedIds.clear();
        this.hasSearched = true;
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load bookings.');
      },
    });
  }

  sortBookings(sort: Sort): void {
    this.sortState = sort;
    this.bookings = this.applySort(this.bookings);
  }

  private applySort(bookings: any[]): any[] {
    return sortTableRows(bookings, this.sortState, {
      bookingId: (booking) => Number(booking.bookingId),
      guestCount: (booking) => Number(booking.guestCount),
      bookingStatusCode: (booking) => booking.bookingStatus ?? booking.bookingStatusCode,
      lcStatusCode: (booking) => booking.lcStatus ?? booking.lcStatusCode,
    });
  }

  reset(): void {
    this.familyCode.setValue('');
    this.fromDate.setValue('');
    this.toDate.setValue('');
    this.guestCount.setValue('');
    this.gender.setValue('');
    this.view.setValue('all');
    this.utsav.setValue('');
    this.search();
  }

  // open the assign-accommodation dialog (Admin + Operator). Confirming is a
  // separate action on this list, so the dialog carries no confirm button.
  assign(booking: any): void {
    if (!this.canAllocate || !this.canAssign(booking)) return;
    this.openAllocateDialog(booking);
  }

  // change a CONFIRMED booking's rooms — same dialog.
  modify(booking: any): void {
    if (!this.canAllocate || !this.canModifyAssignment(booking)) return;
    this.openAllocateDialog(booking);
  }

  // book a mandir utsav on behalf of a devotee
  openManualBooking(): void {
    if (!this.canCreateManual) return;
    const ref = this.dialog.open(ManualBookingDialogComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
    });
    ref.afterClosed().subscribe((created: boolean) => {
      if (created && this.canView) this.search();
    });
  }

  private openAllocateDialog(booking: any): void {
    const ref = this.dialog.open(AllocateDialogComponent, {
      width: '1180px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      data: {
        bookingId: Number(booking.bookingId),
        familyCode: booking.familyCode,
        guestCount: Number(booking.guestCount),
      },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.search();
    });
  }

  // the assigned rooms of a booking; rooms can be released while the booking is
  // pending or confirmed (a confirmed booking with no rooms left falls back to
  // Pending), but not after check-in.
  viewRooms(booking: any): void {
    if (!this.canViewRooms(booking)) return;
    const ref = this.dialog.open(AssignedRoomsDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        bookingId: Number(booking.bookingId),
        familyCode: booking.familyCode,
        canRelease: this.canAllocate
          && this.isAssigned(booking)
          && (this.isPending(booking) || this.isConfirmed(booking)),
      },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.search();
    });
  }

  // confirm a single assigned booking -> CONFIRMED (Admin only)
  confirm(booking: any): void {
    if (!this.canManage || !this.canConfirm(booking)) return;
    this.isLoading = true;
    this.establishmentService.confirmBooking(Number(booking.bookingId)).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Booking confirmed.');
        this.search();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to confirm booking.');
      },
    });
  }

  // confirm every assigned booking in the branch in one go (Admin only)
  confirmAll(): void {
    if (!this.canManage) return;
    this.isLoading = true;
    this.establishmentService.confirmAllBookings().subscribe({
      next: (res: any) => {
        const count = res?.data?.lockedCount ?? 0;
        this.toastService.success(
          count ? `Confirmed ${count} assigned booking(s).` : (res?.message || 'No assigned bookings to confirm.')
        );
        this.search();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to confirm assigned bookings.');
      },
    });
  }

  // confirm only the ticked assigned bookings (Admin only)
  confirmSelected(): void {
    if (!this.canManage || this.selectedIds.size === 0) return;
    this.isLoading = true;
    this.establishmentService.confirmSelectedBookings(Array.from(this.selectedIds)).subscribe({
      next: (res: any) => {
        const count = res?.data?.lockedCount ?? 0;
        const skipped = res?.data?.skippedCount ?? 0;
        this.toastService.success(
          skipped
            ? `Confirmed ${count} booking(s); ${skipped} skipped (no longer assigned).`
            : `Confirmed ${count} booking(s).`
        );
        this.search();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to confirm selected bookings.');
      },
    });
  }

  // revoke every assigned booking's rooms in the branch (Admin only): the beds
  // are freed and the bookings return to Pending.
  revokeAll(): void {
    if (!this.canManage) return;
    this.isLoading = true;
    this.establishmentService.revokeAllAssignments().subscribe({
      next: (res: any) => {
        const count = res?.data?.revokedCount ?? 0;
        this.toastService.success(
          count ? `Revoked rooms for ${count} booking(s).` : (res?.message || 'No assigned bookings to revoke.')
        );
        this.search();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to revoke assigned rooms.');
      },
    });
  }

  // free the rooms of a cancelled / rejected / confirmed booking in one go.
  revoke(booking: any): void {
    if (!this.canAllocate || !this.canRevoke(booking)) return;
    this.isLoading = true;
    this.establishmentService.revokeAssignment(Number(booking.bookingId)).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Assigned rooms revoked.');
        this.search();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to revoke assigned rooms.');
      },
    });
  }

  cancel(booking: any): void {
    if (!this.canManage || !this.canCancelAction(booking)) return;

    this.askReason(`Cancel booking #${booking.bookingId}`, 'Cancel booking', (reason) => {
      this.establishmentService.cancelBooking(Number(booking.bookingId), reason).subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'Booking cancelled.');
          this.search();
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to cancel booking.');
        },
      });
    });
  }

  reject(booking: any): void {
    if (!this.canManage || !this.isPending(booking)) return;

    this.askReason(`Reject booking #${booking.bookingId}`, 'Reject booking', (reason) => {
      this.establishmentService.rejectBooking(Number(booking.bookingId), reason).subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'Booking rejected.');
          this.search();
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to reject booking.');
        },
      });
    });
  }

  // open the reason dialog; run onReason with the entered reason (only if confirmed).
  private askReason(title: string, actionLabel: string, onReason: (reason: string) => void): void {
    const ref = this.dialog.open(BookingActionDialogComponent, {
      width: '480px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100dvh - 32px)',
      data: { title, actionLabel },
    });
    ref.afterClosed().subscribe((reason: string | undefined) => {
      if (!reason) return;
      this.isLoading = true;
      onReason(reason);
    });
  }
}
